/**
 * ```ts
 * interface Separated<E, A> {
 *    readonly left: E
 *    readonly right: A
 * }
 * ```
 *
 * Represents a result of separating a whole into two parts.
 *
 * @since 2.10.0
 */
import { pipe } from './function';
import { flap as flap_ } from './Functor'; // -------------------------------------------------------------------------------------
// constructors
// -------------------------------------------------------------------------------------

/**
 * @category constructors
 * @since 2.10.0
 */

export var separated = function separated(left, right) {
  return {
    left: left,
    right: right
  };
}; // -------------------------------------------------------------------------------------
// non-pipeables
// -------------------------------------------------------------------------------------

var _map = function _map(fa, f) {
  return pipe(fa, map(f));
};

var _mapLeft = function _mapLeft(fa, f) {
  return pipe(fa, mapLeft(f));
};

var _bimap = function _bimap(fa, g, f) {
  return pipe(fa, bimap(g, f));
}; // -------------------------------------------------------------------------------------
// type class members
// -------------------------------------------------------------------------------------

/**
 * `map` can be used to turn functions `(a: A) => B` into functions `(fa: F<A>) => F<B>` whose argument and return types
 * use the type constructor `F` to represent some computational context.
 *
 * @category Functor
 * @since 2.10.0
 */


export var map = function map(f) {
  return function (fa) {
    return separated(left(fa), f(right(fa)));
  };
};
/**
 * Map a function over the first type argument of a bifunctor.
 *
 * @category Bifunctor
 * @since 2.10.0
 */

export var mapLeft = function mapLeft(f) {
  return function (fa) {
    return separated(f(left(fa)), right(fa));
  };
};
/**
 * Map a pair of functions over the two type arguments of the bifunctor.
 *
 * @category Bifunctor
 * @since 2.10.0
 */

export var bimap = function bimap(f, g) {
  return function (fa) {
    return separated(f(left(fa)), g(right(fa)));
  };
}; // -------------------------------------------------------------------------------------
// instances
// -------------------------------------------------------------------------------------

/**
 * @category instances
 * @since 2.10.0
 */

export var URI = 'Separated';
/**
 * @category instances
 * @since 2.10.0
 */

export var Bifunctor = {
  URI: URI,
  mapLeft: _mapLeft,
  bimap: _bimap
};
/**
 * @category instances
 * @since 2.10.0
 */

export var Functor = {
  URI: URI,
  map: _map
};
/**
 * Derivable from `Functor`.
 *
 * @category combinators
 * @since 2.10.0
 */

export var flap = /*#__PURE__*/flap_(Functor); // -------------------------------------------------------------------------------------
// utils
// -------------------------------------------------------------------------------------

/**
 * @since 2.10.0
 */

export var left = function left(s) {
  return s.left;
};
/**
 * @since 2.10.0
 */

export var right = function right(s) {
  return s.right;
};